import { isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { IconButton } from '../../../shared/button/IconButton';
import { Dropdown, DropdownOption } from '../../../shared/dropdown/Dropdown';
import { Search } from '../../../shared/icons';
import styles from './Theoretical.module.scss';
import { NettingEngineSurvey, updateNettingEngineData, NettingEngineCounterparty, setLeiModalOpen, setIsChangingQuestion, NettingEngineAgreement, getCurrentNettingEngineData, ISDANettingEngineData, GMRANettingEngineData } from '../store';
import { NettingEngineEntity } from '../../../admin/entity/store';
import { fetchOpinionSubCounterpartyTypesStarted, resetOpinionSubCounterpartyTypes } from '../../../admin/sub-counterparty-types/store';
import { getUserHasFeaturePermissionNoAdmin } from '../../../auth/login/store';
import { FeaturePermission } from '../../../admin/users/store';
import { CreatableDropdown } from '../../../shared/dropdown/CreateableDropdown';
import { OpinionScope } from '../../../admin/opinions/store';
import { AgreementType } from '../../../admin/documents/store/types';

interface TheoreticalDropdownQuestionsProps {
    surveyQuestionIndex: number;
    nettingEngineSurvey: NettingEngineSurvey[];
    allEntities: NettingEngineEntity[];
    counterpartyNameKnown: boolean | null;
    jurisdictionOptions: { value: string, label: string }[];
    updateCounterpartyOfficeOptions: (value: DropdownOption[]) => void;
    transitionToQuestionByIncrement: (increment?: number) => void
    transitionToNextQuestion: () => void;
    agreementType: AgreementType;
}

export const TheoreticalDropdownQuestions: React.FC<TheoreticalDropdownQuestionsProps> = ({
    surveyQuestionIndex,
    nettingEngineSurvey,
    allEntities,
    counterpartyNameKnown,
    jurisdictionOptions,
    updateCounterpartyOfficeOptions,
    transitionToQuestionByIncrement,
    transitionToNextQuestion,
    agreementType
}) => {
    const currentNettingEngineData = useAppSelector(getCurrentNettingEngineData);
    const counterparty = isNull(currentNettingEngineData) ? null : currentNettingEngineData.counterparty;
    const dispatch = useAppDispatch();

    const subCounterpartyOpinionScope = useMemo(() => {
        switch (agreementType) {
            case AgreementType.GMRA:
                return OpinionScope.GMRA_NETTING;
            case AgreementType.ISDA:
            default:
                return OpinionScope.ISDA_NETTING;
        }
    }, [agreementType]);

    const selectedCounterpartyJurisdiction = useCallback((entityId: string | null) => !isNull(entityId) && allEntities.find(entity => entity.entityId === parseInt(entityId!))?.jurisdiction || undefined, [allEntities]);
    const selectedCounterpartyType = useCallback((entityId: string | null) => {
        if (!isNull(entityId)) {
            const entityInQuestion = allEntities.find(entity => entity.entityId === parseInt(entityId!));
            if (!isUndefined(entityInQuestion)) {
                if (!isNull(entityInQuestion.opinionCounterpartyType)) {
                    const { aiConfirmed, value } = entityInQuestion.opinionCounterpartyType;
                    if (!isNull(value)) {
                        const entitySubCounterpartyTypeId = entityInQuestion.opinionSubCounterpartyTypes.find(({ scope }) => scope.includes(subCounterpartyOpinionScope))?.value || null;
                        return { value, aiConfirmed, subCounterpartyId: entitySubCounterpartyTypeId };
                    }
                }
            }
        }
        return undefined;
    }, [allEntities, subCounterpartyOpinionScope]);

    const updateCounterpartyProperties = useCallback((value: string, key: keyof ISDANettingEngineData | keyof GMRANettingEngineData, counterpartyKey: keyof NettingEngineCounterparty) => {
        if (!isNull(counterparty)) {
            if (counterpartyKey === 'entityId') {
                dispatch(updateNettingEngineData(key, value, counterpartyKey));
                const entityOpinionJurisdiction = selectedCounterpartyJurisdiction(value);
                if (!isUndefined(entityOpinionJurisdiction)) {
                    const matchedJurisdiction = jurisdictionOptions.map(({ value }) => value).find(eachJurisdiction => eachJurisdiction === entityOpinionJurisdiction);
                    if (!isUndefined(matchedJurisdiction)) {
                        dispatch(setIsChangingQuestion(true));
                        dispatch(updateNettingEngineData(key, matchedJurisdiction, 'jurisdiction'));
                        const counterpartyType = selectedCounterpartyType(value);
                        if (!isUndefined(counterpartyType) && counterpartyType.value.length > 0) {
                            const scope = agreementType === AgreementType.ISDA ? OpinionScope.ISDA_NETTING : OpinionScope.GMRA_NETTING;
                            dispatch(fetchOpinionSubCounterpartyTypesStarted(entityOpinionJurisdiction, scope, counterpartyType.value));
                            dispatch(updateNettingEngineData(key, counterpartyType.value, 'counterpartyClassification'));
                            const subCounterpartyId = counterpartyType.subCounterpartyId;
                            if (!isNull(subCounterpartyId)) {
                                dispatch(updateNettingEngineData(key, subCounterpartyId, 'subCounterpartyTypeId'));
                            }
                            if (counterpartyType.aiConfirmed && subCounterpartyId) {
                                transitionToQuestionByIncrement(2);
                            } else {
                                transitionToNextQuestion();
                            }
                        } else {
                            transitionToNextQuestion();
                        }
                    }
                }
            }
            if (['jurisdiction', 'counterpartyClassification'].includes(counterpartyKey)) {
                dispatch(updateNettingEngineData(key, null, 'subCounterpartyTypeId'));
                dispatch(resetOpinionSubCounterpartyTypes());
                if (counterpartyKey === 'jurisdiction') {
                    // if its a new jurisdiction we can fetch the list if the counterparty is defined
                    dispatch(updateNettingEngineData(key, value, 'jurisdiction'));
                    if (!isNull(counterparty.counterpartyClassification)) {
                        const scope = agreementType === AgreementType.ISDA ? OpinionScope.ISDA_NETTING : OpinionScope.GMRA_NETTING;
                        dispatch(fetchOpinionSubCounterpartyTypesStarted(value, scope, counterparty.counterpartyClassification));
                    }
                    transitionToNextQuestion();
                }
                if (counterpartyKey === 'counterpartyClassification' && !isNull(counterparty.jurisdiction)) {
                    // if we're changing the counterparty type then check if we have a jurisdiction
                    const establishIfIncluded = true;
                    dispatch(updateNettingEngineData(key, value, 'counterpartyClassification'));
                    const scope = agreementType === AgreementType.ISDA ? OpinionScope.ISDA_NETTING : OpinionScope.GMRA_NETTING;
                    dispatch(fetchOpinionSubCounterpartyTypesStarted(counterparty.jurisdiction, scope, value, establishIfIncluded));
                }
            }
            if (counterpartyKey === 'subCounterpartyTypeId') {
                dispatch(setIsChangingQuestion(true));
                dispatch(updateNettingEngineData(key, parseInt(value), counterpartyKey));
                transitionToNextQuestion();
            }
        }
    }, [dispatch, jurisdictionOptions, selectedCounterpartyJurisdiction, selectedCounterpartyType, counterparty, transitionToNextQuestion, transitionToQuestionByIncrement, agreementType]);

    const updateDropdownAnswer = useCallback((option: DropdownOption | Options<DropdownOption> | null, key: keyof ISDANettingEngineData | keyof GMRANettingEngineData, counterpartyKey?: keyof NettingEngineCounterparty, isMulti?: boolean) => {
        if (isMulti) {
            let selected: string[] = [];
            if (!isNull(option) && (option as Options<DropdownOption>).length > 0) {
                selected = (option as Options<DropdownOption>).map(({ value }) => value);
            }
            dispatch(updateNettingEngineData(key, selected));
        } else {
            if (!isNull(option)) {
                const value = (option as DropdownOption).value;
                if (!isUndefined(counterpartyKey)) {
                    updateCounterpartyProperties(value, key, counterpartyKey);
                } else {
                    dispatch(updateNettingEngineData(key, value, counterpartyKey));
                    if (key !== 'agreement' || key === 'agreement' && value !== NettingEngineAgreement.ISDA_1992_MASTER) {
                        dispatch(setIsChangingQuestion(true));
                        transitionToNextQuestion();
                    }
                }
            } else {
                if (!isUndefined(counterpartyKey) && ['entityId', 'jurisdiction', 'counterpartyClassification'].includes(counterpartyKey)) {
                    dispatch(updateNettingEngineData(key, null, 'subCounterpartyTypeId'));
                    dispatch(resetOpinionSubCounterpartyTypes());
                }
                dispatch(updateNettingEngineData(key, null, counterpartyKey));
            }
            if (surveyQuestionIndex + 1 === nettingEngineSurvey.length) {
                setTimeout(() => {
                    dispatch(setIsChangingQuestion(false));
                }, 750);
            }
        }
    }, [dispatch, nettingEngineSurvey, surveyQuestionIndex, transitionToNextQuestion, updateCounterpartyProperties]);

    const hasEntityManagementPermission = useAppSelector(getUserHasFeaturePermissionNoAdmin([FeaturePermission.ENTITY_MANAGEMENT]));
    const showLeiSearch = useMemo(() => hasEntityManagementPermission && surveyQuestionIndex === 1 && !!counterpartyNameKnown, [hasEntityManagementPermission, surveyQuestionIndex, counterpartyNameKnown]);

    const openLeiSearch = useCallback(() => dispatch(setLeiModalOpen(true)), [dispatch]);

    const { key, counterpartyKey, value, dropdownOptions, isMulti, closeOnSelect, note, showSubQuestion, subQuestion } = nettingEngineSurvey[surveyQuestionIndex];

    return (
        <>
            <div className={styles.surveyInput}>
                <div className={styles.surveyDropdownWrapper}>
                    {key === 'counterpartyOffices' ? <CreatableDropdown
                        value={(value as DropdownOption)}
                        options={dropdownOptions!}
                        onChange={value => updateDropdownAnswer(value, key, counterpartyKey, isMulti)}
                        updateOptions={updateCounterpartyOfficeOptions}
                        isMulti={isMulti}
                        closeOnSelect={closeOnSelect}
                    /> :
                        <Dropdown
                            value={(value as DropdownOption)}
                            options={dropdownOptions!}
                            onChange={value => updateDropdownAnswer(value, key, counterpartyKey, isMulti)}
                            isMulti={isMulti}
                            closeOnSelect={closeOnSelect}
                        />
                    }
                    {showSubQuestion && !isUndefined(subQuestion) &&
                        <div className={styles.subQuestionWrapper}>
                            <div className={styles.counterpartySubQuestion}>{subQuestion?.question}</div>
                            <Dropdown
                                value={(subQuestion.value as DropdownOption)}
                                options={subQuestion.dropdownOptions!}
                                onChange={val => updateDropdownAnswer(val, subQuestion.key, subQuestion.counterpartyKey)}
                            />
                        </div>
                    }
                </div>
                <div className={styles.inputWrapper}>
                    {showLeiSearch && <IconButton icon={Search} onClick={openLeiSearch} fontSize={35} />}
                </div>
            </div>
            <div className={styles.surveyMessage}>{note || ''}</div>
        </>
    );
};
